import React, { Component } from 'react';
import RouterPropTypes from 'react-router-prop-types';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Alert, Container } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import Page from '../../components/layout/Page';
import { modelOf } from '../../prop-types';
import RouteService from '../../services/RouteService';
import AccountStore from '../../store/AccountStore';
import CurrencyStore from '../../store/CurrencyStore';
import ProposalStore from '../../store/ProposalStore';
import ConfigStore from '../../store/ConfigStore';
import ContentForState from '../../components/loader/ContentForState';
import ScrollReset from '../../components/common/ScrollReset';
import CommonPage from '../../types/CommonPage';
import RequestState from '../../types/RequestState';
import SEOTitle from '../../components/head/SEOTitle';
import RobotsMeta from '../../components/head/RobotsMeta';
import ProposalCheckoutPageContent from '../../components/checkout/ProposalCheckoutPageContent';
import globalTranslations from '../../i18n/globalTranslations';
import Paths from '../../types/Paths';
import generatePath from '../../util/generatePath';
import UnexpectedError from '../../components/loader/UnexpectedError';
import Analytics from '../../analytics/Analytics';
import OrderTotalClass from '../../types/OrderTotalClass';
import { roundWithPrecision } from '../../util/number';

@observer
class CheckoutPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
      confirmState: RequestState.LOADED,
      removeState: RequestState.LOADED,
      toThankYou: false,
    };
    this.maybeLoadProposal();
  }

  maybeLoadProposal = () => {
    const { accountStore, configStore, proposalStore, analytics } = this.props;

    if (!!!window.isSSR) {
      proposalStore.loadProposal().then(() => {
        if (configStore.analytics.ga4.enabled) {
          const withTax = accountStore.showPricesWithTax;
          const cartProducts = proposalStore.getProposalProducts();
          const actionField = { step: 1, option: 'Move To Proposal Request' };
          const filterValue = withTax
            ? proposalStore.proposal.totals.filter(
                (total) => total.class === OrderTotalClass.TOTAL
              )
            : proposalStore.proposal.totals.filter(
                (total) => total.class === OrderTotalClass.TAX_FREE_TOTAL
              );

          const value = filterValue[0] ? filterValue[0].value : 0.0;

          analytics.beginProposal(cartProducts, actionField, value);
        }
      });
    }
  };

  updateCart = () => {
    const { proposalStore } = this.props;

    if (!!!window.isSSR) {
      proposalStore.loadCart();
    }
  };

  confirmOrder = (newOrder) => {
    this.setState({
      confirmState: RequestState.LOADING,
    });
    this.props.proposalStore
      .confirmProposal(newOrder)
      .then(() => {
        this.updateCart();
        this.setState({
          error: null,
          confirmState: RequestState.LOADED,
          toThankYou: true,
        });
      })
      .catch((error) => {
        this.setState({
          error: error.response,
          confirmState: RequestState.ERROR,
        });
      });
  };

  updateOrder = (newOrder) => {
    const { proposalStore } = this.props;

    this.setState({
      confirmState: RequestState.LOADING,
    });

    proposalStore
      .updateProposal(newOrder)
      .then(() => {
        this.updateCart();
        this.setState({
          error: null,
          confirmState: RequestState.LOADED,
        });
      })
      .catch((error) => {
        this.setState({
          error: error.response,
          confirmState: RequestState.ERROR,
        });
      });
  };

  removeProduct = (params) => {
    const { configStore, proposalStore } = this.props;

    this.setState({
      removeState: RequestState.LOADING,
    });

    proposalStore
      .removeProduct(params)
      .then(() => {
        configStore.analytics.ga4.enabled &&
          this.sendRemoveProposalAnalyticsEvent();
        this.updateCart();
        this.setState({
          error: null,
          removeState: RequestState.LOADED,
        });
      })
      .catch((error) => {
        this.setState({
          error: error.response,
          removeState: RequestState.ERROR,
        });
      });
  };

  sendRemoveProposalAnalyticsEvent = () => {
    const { analytics, proposalStore } = this.props;

    const removedProducts = proposalStore.getProposalRemovedProducts();
    const removedProductsValue = removedProducts.reduce((sum, productData) => {
      return sum + productData.finalUnitPrice;
    }, 0);

    analytics.removeFromProposal({
      productList: removedProducts,
      value: roundWithPrecision(removedProductsValue, 4),
    });
  };

  renderError = () => {
    const { error, requestId } = this.state;

    if (!error) {
      return null;
    }

    return error.status === 422 ? (
      <Alert className="CheckoutPage__errors" color="danger" key={requestId}>
        <FormattedMessage {...globalTranslations.formFail422} />
      </Alert>
    ) : (
      <UnexpectedError />
    );
  };

  render() {
    const { proposalStore, routeService, match } = this.props;
    const { toThankYou, confirmState, removeState } = this.state;

    if (!proposalStore) {
      return null;
    }

    if (toThankYou) {
      const { proposal } = proposalStore;

      const url = routeService.getPath(
        generatePath(Paths.ProposalThankYouPage, {
          id: proposal.id,
          trackingCode: proposal.tracking_code,
        })
      );

      window.location.assign(url);
    }

    return (
      <Page name={CommonPage.PROPOSAL_REQUEST} className="CheckoutPage">
        <ScrollReset key={match.params.id} />
        <ContentForState
          state={proposalStore.proposalState}
          error={proposalStore.lastError}
          forLoaded={() => {
            return (
              <>
                <SEOTitle title={{ ...globalTranslations.proposalTitle }} />
                <RobotsMeta noindex />
                <ProposalCheckoutPageContent
                  loading={confirmState === RequestState.LOADING}
                  removing={removeState === RequestState.LOADING}
                  disabled={confirmState === RequestState.ERROR}
                  onOrderConfirm={this.confirmOrder}
                  onOrderUpdate={this.updateOrder}
                  onProductRemove={this.removeProduct}
                />
                <Container className="ProposalCheckoutPageContent__container">
                  {this.renderError()}
                </Container>
              </>
            );
          }}
        />
      </Page>
    );
  }
}

CheckoutPage.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  proposalStore: modelOf(ProposalStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  match: RouterPropTypes.match.isRequired,
};

export default withRouter(
  inject(
    'accountStore',
    'currencyStore',
    'configStore',
    'proposalStore',
    'routeService',
    'analytics'
  )(CheckoutPage)
);
